/**
 * Inline AI model selector for chat conversations.
 *
 * Wraps ModelCategoryPicker in compact mode. Shows the current model name
 * as a clickable button; clicking opens a searchable popover listing
 * available model categories.
 */
import { useTranslation } from 'react-i18next';
import { ModelCategoryPicker } from '../common/ModelCategoryPicker';
import { useAvailableModelCategories } from '../../hooks/useAvailableModelCategories';

interface ChatModelSelectorProps {
  /** Current model category for this session (null = default) */
  sessionModelCategory: string | null;
  /** Display name of the current effective model */
  modelName: string | null;
  /** Whether model selection is allowed */
  allowModelChoice: boolean;
  /** Called when user selects a different model category */
  onModelChange: (categoryId: string | null) => void;
  /** Whether the selector is disabled (e.g., no active session) */
  disabled?: boolean;
}

export const ChatModelSelector: React.FC<ChatModelSelectorProps> = ({
  sessionModelCategory,
  modelName,
  allowModelChoice,
  onModelChange,
  disabled = false,
}) => {
  const { t } = useTranslation('common');
  const { data: categories } = useAvailableModelCategories();

  if (!modelName) return null;

  // No model choice allowed — show static caution text
  if (!allowModelChoice) {
    return (
      <p className="text-center text-xs text-muted-foreground opacity-70">
        {t('ai.modelCaution', { model: modelName })}
      </p>
    );
  }

  return (
    <div className="flex items-center justify-center">
      <ModelCategoryPicker
        value={sessionModelCategory}
        onChange={onModelChange}
        categories={categories ?? []}
        variant="compact"
        disabled={disabled}
        defaultMode="tenant"
        defaultResolvedName={modelName ?? undefined}
        fallbackLabel={modelName ?? undefined}
      />
    </div>
  );
};
